import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  createFinanceExtraIncome,
  deleteFinanceExtraIncome,
  getApiErrorMessage,
  patchFinanceExtraIncome,
} from "../../api";
import ErrorRetry from "../../components/ErrorRetry";
import Loading from "../../components/Loading";
import FinanceTabs from "./FinanceTabs";
import { formatMoney, parseAmount } from "./financeUtils";
import useFinanceOverview from "./useFinanceOverview";

const EMPTY_EXTRA = { amount: "", comment: "", on_date: "", in_plan: false, in_fact: false };

function draftValue(drafts, id, fallback) {
  return Object.prototype.hasOwnProperty.call(drafts, id) ? drafts[id] : fallback;
}

function extraLabel(row) {
  return row.comment || formatMoney(row.amount);
}

export default function FinanceIncome() {
  const { month, setMonth, overview, loading, error, reload } = useFinanceOverview();
  const [extraAmountDrafts, setExtraAmountDrafts] = useState({});
  const [extraCommentDrafts, setExtraCommentDrafts] = useState({});
  const [extraDateDrafts, setExtraDateDrafts] = useState({});
  const [showExtraForm, setShowExtraForm] = useState(false);
  const [extraForm, setExtraForm] = useState(EMPTY_EXTRA);
  const [saveNotice, setSaveNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const extras = overview?.extra_income || [];

  const resetNotices = () => {
    setSaveNotice("");
    setActionError("");
  };

  const afterSave = async () => {
    setSaveNotice("Сохранено");
    await reload();
  };

  const failSave = (err) => {
    setSaveNotice("");
    setActionError(getApiErrorMessage(err, "Не удалось сохранить"));
  };

  const runSave = async (action) => {
    resetNotices();
    try {
      await action();
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const handleDeleteExtra = (row) => {
    if (!window.confirm(`Удалить приход «${extraLabel(row)}»?`)) return;
    return runSave(() => deleteFinanceExtraIncome(row.id));
  };

  const handleExtraAmountBlur = (row) => {
    const parsed = parseAmount(draftValue(
      extraAmountDrafts,
      row.id,
      row.amount == null ? "" : String(row.amount)
    ));
    if (!parsed.ok || parsed.value <= 0) return;
    if (Number(row.amount) === parsed.value) return;
    return runSave(() => patchFinanceExtraIncome(row.id, { amount: parsed.value }));
  };

  const handleExtraCommentBlur = (row) => {
    const comment = String(draftValue(extraCommentDrafts, row.id, row.comment ?? "")).trim();
    const current = String(row.comment ?? "").trim();
    if (comment === current) return;
    return runSave(() => patchFinanceExtraIncome(row.id, { comment: comment || null }));
  };

  const handleExtraDateBlur = (row) => {
    const onDate = String(draftValue(extraDateDrafts, row.id, row.on_date || "")).trim() || null;
    const current = row.on_date || null;
    if (onDate === current) return;
    return runSave(() => patchFinanceExtraIncome(row.id, { on_date: onDate }));
  };

  const handleExtraIncome = async (event) => {
    event.preventDefault();
    const parsed = parseAmount(extraForm.amount);
    if (!parsed.ok || parsed.value <= 0) return;
    if (!extraForm.in_plan && !extraForm.in_fact) return;
    const comment = extraForm.comment.trim();
    const onDate = extraForm.on_date.trim();
    resetNotices();
    try {
      await createFinanceExtraIncome({
        month,
        amount: parsed.value,
        comment: comment || null,
        on_date: onDate || null,
        in_plan: extraForm.in_plan,
        in_fact: extraForm.in_fact,
      });
      setExtraForm(EMPTY_EXTRA);
      setShowExtraForm(false);
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  return (
    <div className="page-container fin-page">
      <FinanceTabs month={month} onMonthChange={setMonth} title="Приход" />
      {error && <ErrorRetry message={error} onRetry={() => reload()} />}
      {saveNotice && (
        <div className="fin-save-ok" role="status">{saveNotice}</div>
      )}
      {actionError && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}
      {loading && !overview ? (
        <Loading text="Загрузка кассы…" />
      ) : overview ? (
        <section className="fin-section" aria-labelledby="fin-income-title">
          <h3 id="fin-income-title">Приход месяца</h3>
          <div className="fin-income">
            <div className="fin-income-row">
              <Link to={`/client-payments?month=${month}`}>Услуги</Link>
              <span>План {formatMoney(overview.income?.services_plan)}</span>
              <span>Факт {formatMoney(overview.income?.services_fact)}</span>
            </div>
            <div className="fin-income-row">
              <Link to="/kontur-payments" translate="no">Контур</Link>
              <span>План {formatMoney(overview.income?.kontur_plan)}</span>
              <span>Факт {formatMoney(overview.income?.kontur_fact)}</span>
            </div>
            <div className="fin-income-row">
              <span>Прочее</span>
              <span>План {formatMoney(overview.income?.extra_plan)}</span>
              <span>Факт {formatMoney(overview.income?.extra_fact)}</span>
            </div>
          </div>
          <ul className="fin-extra-list">
            {extras.map((row) => {
              const label = extraLabel(row);
              return (
                <li key={row.id} className="fin-extra-row">
                  <span>{row.comment || "Прочее"}</span>
                  <label className="login-label">
                    Комментарий {label}
                    <input
                      className="login-input"
                      type="text"
                      value={draftValue(extraCommentDrafts, row.id, row.comment ?? "")}
                      onChange={(e) => {
                        setExtraCommentDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                      }}
                      onBlur={() => handleExtraCommentBlur(row)}
                    />
                  </label>
                  <label className="login-label">
                    Дата {label}
                    <input
                      className="login-input"
                      type="date"
                      value={draftValue(extraDateDrafts, row.id, row.on_date || "")}
                      onChange={(e) => {
                        setExtraDateDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                      }}
                      onBlur={() => handleExtraDateBlur(row)}
                    />
                  </label>
                  <label className="login-label">
                    Сумма {label}
                    <input
                      className="login-input"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={draftValue(
                        extraAmountDrafts,
                        row.id,
                        row.amount == null ? "" : String(row.amount)
                      )}
                      onChange={(e) => {
                        setExtraAmountDrafts((prev) => ({ ...prev, [row.id]: e.target.value }));
                      }}
                      onBlur={() => handleExtraAmountBlur(row)}
                    />
                  </label>
                  <span>
                    {row.in_plan ? "план" : ""}
                    {row.in_plan && row.in_fact ? " / " : ""}
                    {row.in_fact ? "факт" : ""}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => handleDeleteExtra(row)}
                    aria-label={`Удалить ${label}`}
                  >
                    Удалить
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowExtraForm((open) => !open)}
          >
            Добавить приход
          </button>
          {showExtraForm && (
            <form className="fin-inline-form fin-form-card" onSubmit={handleExtraIncome}>
              <label className="login-label">
                Сумма прочего прихода
                <input
                  className="login-input"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={extraForm.amount}
                  onChange={(e) => setExtraForm({ ...extraForm, amount: e.target.value })}
                />
              </label>
              <label className="login-label">
                Комментарий прихода
                <input
                  className="login-input"
                  type="text"
                  value={extraForm.comment}
                  onChange={(e) => setExtraForm({ ...extraForm, comment: e.target.value })}
                />
              </label>
              <label className="login-label">
                Дата прихода
                <input
                  className="login-input"
                  type="date"
                  value={extraForm.on_date}
                  onChange={(e) => setExtraForm({ ...extraForm, on_date: e.target.value })}
                />
              </label>
              <label className="fin-check">
                <input
                  type="checkbox"
                  checked={extraForm.in_plan}
                  onChange={(e) => setExtraForm({ ...extraForm, in_plan: e.target.checked })}
                />
                В план
              </label>
              <label className="fin-check">
                <input
                  type="checkbox"
                  checked={extraForm.in_fact}
                  onChange={(e) => setExtraForm({ ...extraForm, in_fact: e.target.checked })}
                />
                В факт
              </label>
              <button type="submit" className="btn">Создать приход</button>
            </form>
          )}
        </section>
      ) : null}
    </div>
  );
}
